import React, { useEffect, useState, useMemo } from "react";
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { toast } from "sonner";
import { VAULT_ABI, VAULT_ADDRESS, TOKEN_ADDRESS, buyLink } from "../config/contracts";
import { useVaultGlobals, useUserState } from "../hooks/useVault";
import { fmtBnb, fmtToken, fmtSecs, Hourglass, LOGO_URL, GOLD_ZONGZI_URL } from "./Brand";

function PoolDisplay({ pool, totalPaid }) {
  return (
    <div className="glass gold-border p-8 md:p-10 relative overflow-hidden" data-testid="pool-card">
      <div className="absolute -top-6 -right-6 w-44 h-44 md:w-56 md:h-56 opacity-90 pointer-events-none">
        <img
          src="https://customer-assets.emergentagent.com/job_dragon-boat-draw/artifacts/oit5tsuo_%E6%8A%A0%E5%9B%BE%20%285%29.png"
          alt=""
          className="w-full h-full object-contain float-y"
          style={{ filter: "drop-shadow(0 12px 28px rgba(243,186,47,0.35))" }}
        />
      </div>
      <div className="text-emerald-200/70 text-sm font-medium tracking-widest">🌊 当前奖池</div>
      <div className="mt-3 flex items-end gap-3 flex-wrap">
        <div className="font-num text-6xl md:text-7xl lg:text-8xl font-bold gold-shimmer tabular-nums leading-none">
          {fmtBnb(pool, 4)}
        </div>
        <div className="font-display text-2xl md:text-3xl text-amber-300/90 mb-1">BNB</div>
      </div>
      <div className="mt-4 text-emerald-100/70 text-sm md:text-base">
        交易税自动注入，越买奖越大 🌊  
        <span className="ml-2 text-amber-300/80">累计已发 {fmtBnb(totalPaid, 3)} BNB</span>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-emerald-900/30 border border-amber-300/30 px-3 py-2 flex items-center gap-2">
          <img src={LOGO_URL} alt="" className="w-8 h-8 rounded-full"
               style={{ boxShadow: "0 0 0 1.5px rgba(243,186,47,0.55)" }} />
          <div>
            <div className="text-[10px] tracking-widest text-emerald-200/70">1st 幸运粽子</div>
            <div className="flex items-baseline gap-1">
              <span className="font-num text-xl text-amber-300">0.10</span>
              <span className="text-[10px] text-emerald-100/60">BNB</span>
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-emerald-900/30 border border-amber-300/15 px-3 py-2 flex items-center gap-2">
          <img src={GOLD_ZONGZI_URL} alt="" className="w-8 h-8 object-contain" />
          <div>
            <div className="text-[10px] tracking-widest text-emerald-200/70">2nd 安康金粽</div>
            <div className="flex items-baseline gap-1">
              <span className="font-num text-xl text-amber-300">0.05</span>
              <span className="text-[10px] text-emerald-100/60">BNB</span>
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-emerald-900/30 border border-amber-300/15 px-3 py-2 flex items-center gap-2">
          <div className="text-2xl">🍃</div>
          <div>
            <div className="text-[10px] tracking-widest text-emerald-200/70">3rd 吉祥艾草</div>
            <div className="flex items-baseline gap-1">
              <span className="font-num text-xl text-amber-300">0.02</span>
              <span className="text-[10px] text-emerald-100/60">BNB</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, title, status, children, testId }) {
  return (
    <div className={`glass p-5 md:p-6 transition ${status === "active" ? "ring-2 ring-amber-300/50" : ""}`} data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full font-num text-xl flex items-center justify-center font-bold
          ${status === "done" ? "bg-emerald-500/30 text-emerald-200 border border-emerald-300/40" :
            status === "active" ? "bg-amber-300/20 text-amber-200 border border-amber-300/60" :
            "bg-night-900/60 text-emerald-100/40 border border-emerald-100/15"}`}>
          {status === "done" ? "✓" : step}
        </div>
        <div className="flex-1">
          <div className="text-emerald-100/60 text-xs tracking-widest">STEP {step}</div>
          <div className="font-display font-bold text-lg text-white">{title}</div>
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function HeroBoard({ onScrollToSlot }) {
  const { address, isConnected } = useAccount();
  const globals = useVaultGlobals();
  const u = useUserState();

  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const threshold = globals.holdingThreshold ?? 0n;
  const minHold = Number(globals.minHoldTime ?? 900n);
  const cd = Number(globals.cooldown ?? 3600n);

  const myBal = u.tokenBalance ?? u.user?.tokenBalance ?? 0n;
  const eligible = myBal >= threshold;
  const ci = u.checkIn;
  const checkedIn = ci?.checkedIn ?? false;
  const secsLeftCi = ci?.secsLeft ? Number(ci.secsLeft) : (checkedIn ? 0 : minHold);
  const onCooldown = u.user ? now < Number(u.user.nextDrawAt) : false;
  const cooldownLeft = onCooldown ? Number(u.user.nextDrawAt) - now : 0;
  const canDraw = (ci?.canDraw ?? false) && !onCooldown && globals.lotteryEnabled;

  // 写入合约
  const { writeContract, data: txHash, isPending } = useWriteContract();
  const { isLoading: txMining, isSuccess: txDone } = useWaitForTransactionReceipt({ hash: txHash });
  useEffect(() => {
    if (txDone) {
      toast.success("交易已确认，等待链上数据刷新…");
      u.refetch?.();
    }
  }, [txDone]); // eslint-disable-line

  function doCheckIn() {
    if (!VAULT_ADDRESS) {
      toast.info("活动尚未开始，敬请期待 🎋");
      return;
    }
    writeContract({
      address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "checkIn",
    }, {
      onSuccess: () => toast("签到交易已发送…", { description: "等待区块确认" }),
      onError: (e) => toast.error(e?.shortMessage || "签到失败"),
    });
  }
  function doDraw() {
    if (!VAULT_ADDRESS) { toast.info("活动尚未开始，敬请期待 🎋"); return; }
    writeContract({
      address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "draw",
    }, {
      onSuccess: () => { toast("🎲 抽奖交易已发送…", { description: "VRF 回调约 30-90 秒" }); onScrollToSlot?.(); },
      onError: (e) => toast.error(e?.shortMessage || "抽奖失败"),
    });
  }

  // 状态判断
  const stepEligible = eligible ? "done" : "active";
  const stepCheckIn = !eligible ? "todo" : (checkedIn && secsLeftCi > 0 ? "active" : (checkedIn ? "done" : "active"));
  const stepDraw = canDraw ? "active" : "todo";

  // 进度（签到 15min 倒计时进度）
  const ciProgress = checkedIn
    ? Math.min(100, ((minHold - secsLeftCi) / minHold) * 100)
    : 0;

  return (
    <section id="hero" className="relative z-10 grid lg:grid-cols-5 gap-6 md:gap-8 px-6 md:px-10 mt-4">
      <div className="lg:col-span-3">
        <PoolDisplay pool={globals.stats?.poolBalance} totalPaid={globals.stats?.totalPrizesPaid} />

        {/* 全局统计条 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {[
            { l: "累计抽奖", v: globals.stats ? globals.stats.totalDraws.toString() : "—", suf: "次" },
            { l: "一等中奖", v: globals.stats ? globals.stats.tier1Wins.toString() : "—", suf: "次", color: "text-amber-300" },
            { l: "二等中奖", v: globals.stats ? globals.stats.tier2Wins.toString() : "—", suf: "次", color: "text-emerald-300" },
            { l: "三等中奖", v: globals.stats ? globals.stats.tier3Wins.toString() : "—", suf: "次", color: "text-teal-300" },
          ].map((x, i) => (
            <div key={i} className="glass-dark px-4 py-3" data-testid={`stat-card-${i}`}>
              <div className="text-[11px] tracking-widest text-emerald-200/60">{x.l}</div>
              <div className="font-num text-2xl tabular-nums mt-1">
                <span className={x.color || "text-white"}>{x.v}</span>
                <span className="text-xs text-emerald-100/50 ml-1">{x.suf}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 右侧：3 步走面板 */}
      <div className="lg:col-span-2 space-y-4" id="draw-panel">
        <div className="glass-dark px-5 py-4 flex items-center gap-3">
          <div className="text-2xl">🎯</div>
          <div>
            <div className="font-display text-lg text-amber-300">3 步开抽</div>
            <div className="text-xs text-emerald-100/60">免费抽奖，公平开奖，BNB 自动到账</div>
          </div>
        </div>

        <StepCard step="1" title="持仓校验" status={stepEligible} testId="step-1-card">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <div className="text-xs text-emerald-100/60">当前持仓</div>
              <div className="font-num text-2xl tabular-nums">
                {isConnected ? fmtToken(myBal) : "—"}
                <span className="text-xs text-emerald-100/50 ml-1">/ {fmtToken(threshold)}</span>
              </div>
            </div>
            {eligible ? (
              <span className="text-emerald-300 text-sm flex items-center gap-1">✓ 已达标</span>
            ) : (
              <a href={TOKEN_ADDRESS ? buyLink(TOKEN_ADDRESS) : "#"} target="_blank" rel="noreferrer"
                 className="btn-jade text-sm" data-testid="btn-buy-token">前往购买</a>
            )}
          </div>
        </StepCard>

        <StepCard step="2" title="签到锁仓 15 分钟" status={stepCheckIn} testId="step-2-card">
          <div className="flex items-center gap-4">
            <Hourglass className="w-12 h-12 shrink-0" running={checkedIn && secsLeftCi > 0} />
            <div className="flex-1">
              {!checkedIn && (
                <>
                  <div className="text-sm text-emerald-100/70">点击下方签到，记录持仓快照</div>
                  <button onClick={doCheckIn} disabled={!eligible || isPending || txMining}
                    className="btn-jade mt-2 w-full" data-testid="btn-checkin">
                    {isPending || txMining ? "处理中…" : "📝 立即签到"}
                  </button>
                </>
              )}
              {checkedIn && secsLeftCi > 0 && (
                <>
                  <div className="text-xs text-emerald-100/60">还需等待</div>
                  <div className="font-num text-3xl tabular-nums text-amber-300">{fmtSecs(secsLeftCi)}</div>
                  <div className="mt-2 h-1.5 rounded-full bg-emerald-900/40 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all"
                         style={{ width: `${ciProgress}%` }} />
                  </div>
                </>
              )}
              {checkedIn && secsLeftCi === 0 && (
                <>
                  <div className="text-emerald-300 text-sm">✓ 签到生效，可立即抽奖</div>
                  <button onClick={doCheckIn} className="text-xs text-emerald-200/60 mt-1 underline" data-testid="btn-recheckin">
                    重新签到（更新持仓快照）
                  </button>
                </>
              )}
            </div>
          </div>
        </StepCard>

        <StepCard step="3" title="抽奖" status={stepDraw} testId="step-3-card">
          {onCooldown ? (
            <>
              <div className="text-xs text-emerald-100/60">距离下次抽奖</div>
              <div className="font-num text-3xl tabular-nums text-amber-300">{fmtSecs(cooldownLeft)}</div>
              <div className="text-xs text-emerald-100/50 mt-2">冷却 {Math.floor(cd / 60)} 分钟</div>
            </>
          ) : (
            <>
              <div className="text-xs text-emerald-100/70">点击抽奖，30-90 秒后中奖 BNB 自动到账</div>
              <button onClick={doDraw} disabled={!canDraw || isPending || txMining}
                className="btn-ribbon w-full mt-3 text-lg" data-testid="btn-draw">
                {isPending || txMining ? "提交交易…" : (canDraw ? "🎲 抽 BNB" : "未满足条件")}
              </button>
              {!isConnected && <div className="text-xs text-emerald-100/50 mt-2">请先连接钱包</div>}
            </>
          )}
        </StepCard>
      </div>
    </section>
  );
}
